<template>
    <AppDialog v-model="isOpen" size="lg" @close="resetForm">
        <template #header>
            <h2 class="text-lg font-semibold text-theme-base">
                {{ modalTitle }}
            </h2>
            <p v-if="needsTypeStep" class="mt-1 text-sm text-theme-muted">
                Elige qué quieres crear.
            </p>
        </template>

        <div v-if="needsTypeStep" class="space-y-2">
            <button
                v-for="option in linkTypeOptions"
                :key="option.value"
                type="button"
                class="flex w-full items-start gap-3 rounded-xl border border-theme-border p-3 text-left transition hover:border-theme-primary hover:bg-theme-hover"
                @click="selectLinkType(option.value)"
            >
                <span class="text-xl">{{ option.icon }}</span>
                <span class="min-w-0 flex-1">
                    <span class="block text-sm font-semibold text-theme-base">
                        {{ option.label }}
                    </span>
                    <span class="block text-xs text-theme-muted">
                        {{ option.description }}
                    </span>
                </span>
            </button>
        </div>

        <div v-else class="space-y-4">
            <div v-if="isBanner">
                <label class="text-sm text-theme-muted block mb-2">
                    Imagen *
                </label>
                <image-manage v-model="form.image" :enabled-create="true" />
                <p v-if="errors.image" class="text-theme-danger text-xs mt-1">
                    {{ errors.image }}
                </p>
            </div>

            <custom-input
                v-model="form.title"
                label="Título *"
                maxlength="120"
                :error="errors.title || ''"
            />

            <template v-if="isBanner">
                <div>
                    <custom-checkbox v-model="hasDestination" primary>
                        Agregar un enlace de destino
                    </custom-checkbox>
                    <p
                        v-if="!hasDestination"
                        class="text-xs text-theme-muted mt-1"
                    >
                        La imagen se mostrará sola, sin llevar a ningún lado al
                        hacer clic.
                    </p>

                    <div v-else class="mt-3 min-h-96">
                        <label class="text-sm text-theme-muted block mb-1">
                            Destino
                        </label>
                        <div class="flex flex-wrap gap-2 mb-3">
                            <button
                                v-for="option in destinationOptions"
                                :key="option.value"
                                type="button"
                                class="flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition"
                                :class="
                                    form.entity === option.value
                                        ? 'border-theme-primary bg-theme-hover text-theme-primary'
                                        : 'border-theme-border text-theme-muted'
                                "
                                @click="setDestinationEntity(option.value)"
                            >
                                {{ option.label }}
                            </button>
                        </div>

                        <products-search
                            v-if="form.entity === 'product'"
                            paginated
                            :item-current="{
                                name: form.entityProduct
                                    ? form.entityProduct.name
                                    : ''
                            }"
                            @input="onDestinationProductSelected"
                        />

                        <line-selector
                            v-if="form.entity === 'line'"
                            :item-current="{
                                name: form.entityLine
                                    ? form.entityLine.name
                                    : '',
                                slug: form.entityLine
                                    ? form.entityLine.slug
                                    : ''
                            }"
                            @change="onDestinationLineSelected"
                        />
                        <button
                            v-if="form.entity === 'line' && lineImages.length"
                            type="button"
                            class="mt-2 text-xs font-medium text-theme-primary hover:underline"
                            @click="useLineImage"
                        >
                            Usar la imagen de la categoría
                        </button>

                        <custom-input
                            v-if="form.entity === 'external'"
                            v-model="form.url"
                            label="URL *"
                            placeholder="https://..."
                            :error="errors.url || ''"
                        />
                        <p
                            v-else-if="errors.url"
                            class="text-theme-danger text-xs mt-1"
                        >
                            {{ errors.url }}
                        </p>
                    </div>
                </div>
            </template>
            <template v-else>
                <custom-input
                    v-model="form.url"
                    label="URL *"
                    placeholder="https://..."
                    :error="errors.url || ''"
                />

                <div>
                    <custom-checkbox v-model="includeImage" primary>
                        Incluir imagen
                    </custom-checkbox>

                    <div v-if="includeImage" class="mt-3">
                        <image-manage
                            v-model="form.image"
                            :enabled-create="true"
                        />
                    </div>
                </div>
            </template>
        </div>

        <template #footer>
            <div class="flex flex-col sm:flex-row justify-end gap-2">
                <button
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="isOpen = false"
                >
                    Cancelar
                </button>
                <button
                    v-if="canGoBackToTypeStep"
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="chosenType = null"
                >
                    Volver
                </button>
                <app-button
                    v-if="!needsTypeStep"
                    color="success"
                    :loading="isSaving"
                    @click="submit"
                >
                    {{ submitLabel }}
                </app-button>
            </div>
        </template>
    </AppDialog>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import { mapActions, mapGetters } from "vuex";

import { ensureUrlProtocol } from "@/common/lib/url";

import LineSelector from "@/components/lines/LineSelector.vue";
import ProductsSearch from "@/components/products/ProductsSearch.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomCheckbox from "@/components/utils/CustomCheckbox.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImageManage from "@/components/utils/ImageManage.vue";

const DEFAULT_FORM = (entity) => ({
    title: "",
    url: "",
    image: null,
    entity,
    entityProduct: null,
    entityLine: null
});

const DESTINATION_OPTIONS = [
    { value: "product", label: "Producto" },
    { value: "line", label: "Categoría" },
    { value: "external", label: "Enlace externo" }
];

const LINK_TYPE_OPTIONS = [
    {
        value: "link",
        label: "Link / Botón",
        icon: "🔗",
        description: "Un botón con texto e imagen chica opcional."
    },
    {
        value: "banner",
        label: "Banner",
        icon: "🖼️",
        description: "Una imagen con enlace de destino opcional."
    }
];

export default {
    name: "CreateCustomLinkModal",
    components: {
        AppDialog,
        CustomCheckbox,
        CustomInput,
        ImageManage,
        LineSelector,
        ProductsSearch
    },
    mixins: [planLimitError],
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        segmentType: {
            type: String,
            default: "links"
        },
        link: {
            type: Object,
            default: null
        },
        askType: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: DEFAULT_FORM("external"),
            errors: {},
            isSaving: false,
            includeImage: false,
            chosenType: null,
            lineImages: []
        };
    },
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        isBanner() {
            if (this.askType) return this.chosenType === "banner";
            return this.segmentType === "banners";
        },
        isEditing() {
            return !!this.link;
        },
        // Banners desde Starter: sin el permiso se crea directo un enlace,
        // sin pasar por la elección de tipo.
        canCreateBanner() {
            return this.$ability.hasPermission("customLink:CreateBanner");
        },
        needsTypeStep() {
            return this.askType && this.canCreateBanner && !this.chosenType;
        },
        canGoBackToTypeStep() {
            return (
                this.askType &&
                this.canCreateBanner &&
                !this.isEditing &&
                !this.needsTypeStep
            );
        },
        linkTypeOptions() {
            return LINK_TYPE_OPTIONS;
        },
        modalTitle() {
            if (this.needsTypeStep) return "Nuevo enlace";
            if (this.isEditing) {
                return this.isBanner ? "Editar imagen" : "Editar enlace";
            }
            return this.isBanner ? "Crear imagen" : "Crear enlace";
        },
        submitLabel() {
            if (this.isEditing) return "Guardar cambios";
            return this.isBanner ? "Crear banner" : "Crear enlace";
        },
        destinationOptions() {
            return DESTINATION_OPTIONS;
        },
        hasDestination: {
            get() {
                return this.form.entity !== "none";
            },
            set(value) {
                this.setDestinationEntity(
                    value ? DESTINATION_OPTIONS[0].value : "none"
                );
            }
        }
    },
    watch: {
        value(isOpen) {
            if (isOpen) this.prefillForm();
        }
    },
    methods: {
        ...mapActions("customLinks", ["createCustomLink", "updateCustomLink"]),
        ...mapActions("lines", ["getLineBySlug"]),
        resetForm() {
            this.form = DEFAULT_FORM("external");
            this.includeImage = false;
            this.errors = {};
            this.chosenType = null;
            this.lineImages = [];
        },
        prefillForm() {
            if (!this.link) {
                this.resetForm();
                return;
            }
            this.form = {
                title: this.link.title || "",
                url: this.link.url || "",
                image: this.link.image || null,
                entity: this.link.entity || "external",
                entityProduct: null,
                entityLine: null
            };
            this.includeImage = !!this.form.image;
            this.errors = {};
            this.chosenType = this.link.type === "banner" ? "banner" : "link";
            this.lineImages = [];
        },
        selectLinkType(type) {
            this.chosenType = type;
        },
        setDestinationEntity(entity) {
            this.form.entity = entity;
            this.form.entityProduct = null;
            this.form.entityLine = null;
            this.form.url = "";
            this.lineImages = [];
        },
        onDestinationProductSelected(product) {
            if (!product?.slug) return;
            this.form.entityProduct = product;
            this.form.url = `/${product.slug}`;
        },
        async onDestinationLineSelected(line) {
            if (!line?.slug) return;
            this.form.entityLine = line;
            this.form.url = `/lineas/${line.slug}`;
            this.lineImages = [];
            try {
                const lineDetail = await this.getLineBySlug({
                    slug: line.slug
                });
                this.lineImages = lineDetail.images || [];
            } catch {
                this.lineImages = [];
            }
        },
        useLineImage() {
            if (!this.lineImages.length) return;
            this.form.image = this.lineImages[0];
        },
        validateForm() {
            this.errors = {};
            if (!this.isBanner || this.form.entity === "external") {
                this.form.url = ensureUrlProtocol(this.form.url);
            }
            if (!this.form.title.trim()) {
                this.errors.title = "El título es obligatorio";
            }
            if (this.isBanner) {
                if (!this.form.image) {
                    this.errors.image = "Selecciona una imagen para el banner";
                }
                if (this.form.entity !== "none" && !this.form.url.trim()) {
                    this.errors.url =
                        this.form.entity === "external"
                            ? "La URL es obligatoria"
                            : "Selecciona un producto o línea";
                }
            } else if (!this.form.url.trim()) {
                this.errors.url = "La URL es obligatoria";
            }
            return Object.keys(this.errors).length === 0;
        },
        async submit() {
            if (!this.validateForm() || this.isSaving) return;

            this.isSaving = true;
            try {
                const payload = {
                    title: this.form.title.trim()
                };
                if (!this.isEditing) {
                    payload.type = this.isBanner ? "banner" : "link";
                }
                if (this.form.entity !== "none") {
                    payload.url = this.form.url.trim();
                }
                if (this.isBanner) {
                    payload.entity = this.form.entity;
                }
                if (this.form.image && (this.isBanner || this.includeImage)) {
                    payload.imageId = this.form.image.id;
                }
                const isEditing = this.isEditing;
                let saved;
                if (isEditing) {
                    // PATCH /bo/custom-links/:id responde sin cuerpo: el
                    // link actualizado se arma con lo que se acaba de enviar.
                    await this.updateCustomLink({
                        id: this.link.id,
                        ...payload
                    });
                    saved = {
                        id: this.link.id,
                        title: payload.title,
                        url: payload.url ?? this.link.url,
                        entity: payload.entity ?? this.link.entity,
                        image: payload.imageId
                            ? this.form.image
                            : this.link.image
                    };
                } else {
                    saved = await this.createCustomLink(payload);
                }
                this.isSaving = false;
                this.isOpen = false;
                this.$sound.play("success");
                this.$emit(isEditing ? "updated" : "created", {
                    id: saved.id,
                    title: saved.title,
                    url: saved.url,
                    entity: saved.entity,
                    type: this.isBanner ? "banner" : "link",
                    image: saved.image ?? null,
                    imageUrl: saved.image?.url ?? null
                });
            } catch (error) {
                this.isSaving = false;
                if (this.openPlanUpgradeForLimit(error)) {
                    this.isOpen = false;
                    return;
                }
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
